export const QUOTE_WINDOW_OFFSETS = [0, 1, 3, 5, 10, 15] as const;
export const QUOTE_WINDOW_MAX_BYTES = 65_536;
export const QUOTE_WINDOW_INPUT_VERSION = 'tsm-radar-quote-window-input-v1' as const;
export const QUOTE_WINDOW_EXPORT_VERSION = 'tsm-radar-quote-window-export-v1' as const;
export const QUOTE_WINDOW_METHOD = 'tsm-radar-quote-window-v1' as const;

export type QuoteWindowSample = {
  symbol: string;
  bucketStartMs: number;
  dataQuality: 'scan' | 'watch' | 'deep';
  published: boolean;
  sampleCount: number | null;
  bid: number | null;
  ask: number | null;
  quoteTimestampMs: number | null;
  observedAtMs: number;
};

export type QuoteWindowInput = {
  version: typeof QUOTE_WINDOW_INPUT_VERSION;
  venue: 'bitget';
  market: 'USDT-FUTURES';
  quoteUnit: 'USDT';
  symbol: string;
  at: string;
  readAt: string;
  samples: QuoteWindowSample[];
};

export type QuoteWindowSlot = {
  offsetMinutes: number;
  bucketStartMs: number;
  source: QuoteWindowSample | null;
  status: 'available' | 'unavailable';
  reason: string | null;
  midpoint: number | null;
};

export type QuoteWindowComparison = {
  offsetMinutes: number;
  status: 'available' | 'unavailable';
  reason: string | null;
  midpointChangeBps: number | null;
  elapsedSampleMs: number | null;
};

export type QuoteWindowResult = {
  method: typeof QUOTE_WINDOW_METHOD;
  status: 'complete' | 'partial' | 'unavailable';
  slots: QuoteWindowSlot[];
  comparisons: QuoteWindowComparison[];
  limitations: string[];
};

export type QuoteWindowPacket = {
  version: typeof QUOTE_WINDOW_EXPORT_VERSION;
  input: QuoteWindowInput;
  result: QuoteWindowResult;
  digest: string;
};

const LIMITATIONS = Object.freeze([
  'Samples are retained Bitget USDT-FUTURES ticker observations for exact requested minute buckets only.',
  'Provider timestamps are snapshot clocks and do not prove when bid or ask last changed or when data became publicly available.',
  'Worker observed clocks are sampled before durable batch persistence and do not prove receipt or durable availability.',
  'Missing and invalid samples are not filled; unrequested minutes and continuous path completeness are not assessed.',
  'Changes compare later sample bucket midpoints and are not executable returns, forecasts, causal effects, or evidence of signal performance.',
]);

const INPUT_KEYS = ['version', 'venue', 'market', 'quoteUnit', 'symbol', 'at', 'readAt', 'samples'];
const SAMPLE_KEYS = ['symbol', 'bucketStartMs', 'dataQuality', 'published', 'sampleCount', 'bid', 'ask', 'quoteTimestampMs', 'observedAtMs'];

function isRecord(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function hasExactKeys(value: Record<string, unknown>, keys: string[]): boolean {
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
}

function isEpochMs(value: unknown): value is number {
  return Number.isSafeInteger(value) && (value as number) >= 0 && (value as number) <= 8_640_000_000_000_000;
}

function isNullableFinite(value: unknown): value is number | null {
  return value === null || (typeof value === 'number' && Number.isFinite(value));
}

function canonicalIsoMs(value: unknown): number | null {
  if (typeof value !== 'string') return null;
  const ms = Date.parse(value);
  if (!Number.isSafeInteger(ms) || ms < 0 || ms > 8_640_000_000_000_000) return null;
  try { return new Date(ms).toISOString() === value ? ms : null; } catch { return null; }
}

export function normalizeQuoteWindowSymbol(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toUpperCase();
  if (!/^[\p{L}\p{N}][\p{L}\p{N}._-]{1,31}$/u.test(normalized)) return null;
  return normalized;
}

export function parseQuoteWindowAt(value: unknown, nowMs: number): number | null {
  const ms = canonicalIsoMs(value);
  if (ms === null || ms % 60_000 !== 0 || !Number.isFinite(nowMs) || !Number.isSafeInteger(nowMs)) return null;
  return ms + 60_000 <= nowMs ? ms : null;
}

function validateSample(value: unknown, symbol: string, atMs: number): QuoteWindowSample {
  if (!isRecord(value) || !hasExactKeys(value, SAMPLE_KEYS)) throw new Error('Invalid quote-window input.');
  if (value.symbol !== symbol || typeof value.bucketStartMs !== 'number' || !isEpochMs(value.bucketStartMs)) throw new Error('Invalid quote-window input.');
  if (typeof value.dataQuality !== 'string' || !['scan', 'watch', 'deep'].includes(value.dataQuality) || typeof value.published !== 'boolean') throw new Error('Invalid quote-window input.');
  if (!(value.sampleCount === null || (Number.isSafeInteger(value.sampleCount) && (value.sampleCount as number) >= 0))) throw new Error('Invalid quote-window input.');
  if (!isNullableFinite(value.bid) || !isNullableFinite(value.ask)) throw new Error('Invalid quote-window input.');
  if (!(value.quoteTimestampMs === null || isEpochMs(value.quoteTimestampMs)) || !isEpochMs(value.observedAtMs)) throw new Error('Invalid quote-window input.');
  const allowedBuckets = QUOTE_WINDOW_OFFSETS.map(offset => atMs + offset * 60_000);
  if (!allowedBuckets.includes(value.bucketStartMs)) throw new Error('Invalid quote-window input.');
  return {
    symbol,
    bucketStartMs: value.bucketStartMs,
    dataQuality: value.dataQuality as QuoteWindowSample['dataQuality'],
    published: value.published,
    sampleCount: value.sampleCount as number | null,
    bid: value.bid,
    ask: value.ask,
    quoteTimestampMs: value.quoteTimestampMs as number | null,
    observedAtMs: value.observedAtMs,
  };
}

export function validateQuoteWindowInput(value: unknown): QuoteWindowInput {
  if (!isRecord(value) || !hasExactKeys(value, INPUT_KEYS) || value.version !== QUOTE_WINDOW_INPUT_VERSION || value.venue !== 'bitget' || value.market !== 'USDT-FUTURES' || value.quoteUnit !== 'USDT') {
    throw new Error('Invalid quote-window input.');
  }
  const symbol = normalizeQuoteWindowSymbol(value.symbol);
  const atMs = canonicalIsoMs(value.at);
  const readAtMs = canonicalIsoMs(value.readAt);
  if (!symbol || symbol !== value.symbol || atMs === null || readAtMs === null || atMs % 60_000 !== 0 || atMs + 60_000 > readAtMs || !Array.isArray(value.samples) || Object.getPrototypeOf(value.samples) !== Array.prototype || value.samples.length > 6) {
    throw new Error('Invalid quote-window input.');
  }
  const sampleKeys = Object.keys(value.samples);
  if (sampleKeys.length !== value.samples.length || sampleKeys.some((key, index) => key !== String(index))) throw new Error('Invalid quote-window input.');
  const samples = value.samples.map(sample => validateSample(sample, symbol, atMs));
  for (let index = 1; index < samples.length; index += 1) {
    if (samples[index - 1].bucketStartMs >= samples[index].bucketStartMs) throw new Error('Invalid quote-window input.');
  }
  return {
    version: QUOTE_WINDOW_INPUT_VERSION,
    venue: 'bitget',
    market: 'USDT-FUTURES',
    quoteUnit: 'USDT',
    symbol,
    at: value.at as string,
    readAt: value.readAt as string,
    samples,
  };
}

function slotFor(input: QuoteWindowInput, offsetMinutes: number): QuoteWindowSlot {
  const bucketStartMs = canonicalIsoMs(input.at)! + offsetMinutes * 60_000;
  const source = input.samples.find(sample => sample.bucketStartMs === bucketStartMs) ?? null;
  const unavailable = (reason: string): QuoteWindowSlot => ({ offsetMinutes, bucketStartMs, source, status: 'unavailable', reason, midpoint: null });
  const readAtMs = canonicalIsoMs(input.readAt)!;
  if (bucketStartMs + 60_000 > readAtMs) return unavailable('not-yet-complete');
  if (!source) return unavailable('missing-row');
  if (source.observedAtMs < bucketStartMs + 60_000 || source.observedAtMs > readAtMs) return unavailable('invalid-observation-clock');
  if (source.bid === null || source.ask === null || source.quoteTimestampMs === null) return unavailable('missing-quote');
  if (source.bid <= 0 || source.ask <= 0 || source.bid > source.ask) return unavailable('invalid-quote');
  if (source.quoteTimestampMs < bucketStartMs || source.quoteTimestampMs >= bucketStartMs + 60_000 || source.quoteTimestampMs > source.observedAtMs) return unavailable('invalid-quote-clock');
  const midpoint = source.bid / 2 + source.ask / 2;
  if (!Number.isFinite(midpoint) || midpoint <= 0) return unavailable('calculation-unavailable');
  return { offsetMinutes, bucketStartMs, source, status: 'available', reason: null, midpoint };
}

function compareSlots(base: QuoteWindowSlot, endpoint: QuoteWindowSlot): QuoteWindowComparison {
  const unavailable = (reason: string): QuoteWindowComparison => ({ offsetMinutes: endpoint.offsetMinutes, status: 'unavailable', reason, midpointChangeBps: null, elapsedSampleMs: null });
  if (base.status !== 'available' || base.source?.quoteTimestampMs === null || base.source === null) return unavailable('baseline-unavailable');
  if (endpoint.status !== 'available' || endpoint.source?.quoteTimestampMs === null || endpoint.source === null) return unavailable(endpoint.reason ?? 'endpoint-unavailable');
  const elapsedSampleMs = endpoint.source.quoteTimestampMs - base.source.quoteTimestampMs;
  if (elapsedSampleMs <= 0) return unavailable('invalid-sample-order');
  const midpointChangeBps = 10_000 * (endpoint.midpoint! / base.midpoint! - 1);
  if (!Number.isFinite(midpointChangeBps)) return unavailable('calculation-unavailable');
  return { offsetMinutes: endpoint.offsetMinutes, status: 'available', reason: null, midpointChangeBps, elapsedSampleMs };
}

export function calculateQuoteWindow(value: unknown): QuoteWindowResult {
  const input = validateQuoteWindowInput(value);
  const slots = QUOTE_WINDOW_OFFSETS.map(offset => slotFor(input, offset));
  const comparisons = slots.slice(1).map(slot => compareSlots(slots[0], slot));
  const available = comparisons.filter(comparison => comparison.status === 'available').length;
  return {
    method: QUOTE_WINDOW_METHOD,
    status: available === 5 ? 'complete' : available > 0 ? 'partial' : 'unavailable',
    slots,
    comparisons,
    limitations: [...LIMITATIONS],
  };
}

function codePointCompare(left: string, right: string): number {
  const a = Array.from(left, char => char.codePointAt(0)!);
  const b = Array.from(right, char => char.codePointAt(0)!);
  const length = Math.min(a.length, b.length);
  for (let i = 0; i < length; i += 1) if (a[i] !== b[i]) return a[i] - b[i];
  return a.length - b.length;
}

function canonicalize(value: unknown): string {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return JSON.stringify(value);
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new Error('Invalid canonical JSON value.');
    return Object.is(value, -0) ? '0' : JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    const keys = Object.keys(value);
    const ownKeys = Reflect.ownKeys(value);
    if (Object.getPrototypeOf(value) !== Array.prototype || keys.length !== value.length || ownKeys.length !== value.length + 1 || keys.some((key, index) => key !== String(index))) throw new Error('Invalid canonical JSON value.');
    return `[${Array.from({ length: value.length }, (_, index) => canonicalize(value[index])).join(',')}]`;
  }
  if (isRecord(value)) {
    const keys = Object.keys(value).sort(codePointCompare);
    if (Reflect.ownKeys(value).length !== keys.length) throw new Error('Invalid canonical JSON value.');
    return `{${keys.map(key => `${JSON.stringify(key)}:${canonicalize(value[key])}`).join(',')}}`;
  }
  throw new Error('Invalid canonical JSON value.');
}

export function canonicalQuoteWindowJson(value: unknown): string {
  return canonicalize(value);
}

